import { useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../../../components/ui/card';
import { SearchType } from '../types';

interface IndividualSearchProps {
  onSearch: (searchType: SearchType, searchValue: string) => void;
  isSearching: boolean;
}

const searchTypeLabels: Record<SearchType, string> = {
  cpf: 'CPF',
  cnpj: 'CNPJ',
  phone: 'Telefone',
};

const searchTypePlaceholders: Record<SearchType, string> = {
  cpf: '000.000.000-00',
  cnpj: '00.000.000/0000-00',
  phone: '(00) 00000-0000',
};

export function IndividualSearch({
  onSearch,
  isSearching,
}: IndividualSearchProps) {
  const [searchType, setSearchType] = useState<SearchType>('cpf');
  const [searchValue, setSearchValue] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchValue.trim()) {
      return;
    }
    onSearch(searchType, searchValue);
  };

  const formatValue = (value: string, type: SearchType): string => {
    const numbers = value.replace(/\D/g, '');

    if (type === 'cpf') {
      return numbers
        .slice(0, 11)
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }

    if (type === 'cnpj') {
      return numbers
        .slice(0, 14)
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }

    if (type === 'phone') {
      return numbers
        .slice(0, 11)
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
    }

    return value;
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatValue(e.target.value, searchType);
    setSearchValue(formatted);
  };

  const handleTypeChange = (value: SearchType) => {
    setSearchType(value);
    setSearchValue('');
  };

  return (
    <Card className="animate-in fade-in-50 duration-300">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5" />
          Busca Individual
        </CardTitle>
        <CardDescription>
          Busque um cadastro por CPF, CNPJ ou telefone para visualizar e editar
          informações
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:flex-row">
          <Select value={searchType} onValueChange={handleTypeChange}>
            <SelectTrigger className="w-full sm:w-[140px]">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(searchTypeLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            type="text"
            placeholder={searchTypePlaceholders[searchType]}
            value={searchValue}
            onChange={handleValueChange}
            className="flex-1"
          />

          <Button type="submit" disabled={isSearching || !searchValue.trim()}>
            {isSearching ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Buscando...
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                Buscar
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
